import { useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Inline message editor — an auto-growing textarea with Discord-style keys
 * (Enter saves, Shift+Enter newline, Esc cancels). Extracted from MessagesList
 * so the row component stays focused on rendering.
 */
export function EditForm(props: {
  draft: string;
  setDraft: (v: string) => void;
  originalContent: string;
  onSave: (next: string) => Promise<void>;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Auto-grow: shrink to scrollHeight on every draft change so multi-line
  // edits don't get clipped or hidden behind a tiny single-line input.
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 240) + 'px';
  }, [props.draft]);
  const submit = async () => {
    const next = props.draft.trim();
    if (next && next !== props.originalContent) {
      await props.onSave(next);
    } else {
      props.onCancel();
    }
  };
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <textarea
        ref={textareaRef}
        value={props.draft}
        autoFocus
        rows={1}
        aria-label={t('messages.editMessage')}
        onChange={(e) => props.setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.nativeEvent.isComposing) return;
          if (e.key === 'Escape') {
            e.preventDefault();
            props.onCancel();
            return;
          }
          // Enter without Shift = submit (Discord behavior); Shift+Enter = newline.
          // Skip while IME composition is active so Japanese/Chinese commit Enter
          // doesn't accidentally save the message mid-word.
          if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault();
            void submit();
          }
        }}
        className="w-full resize-none rounded bg-black/30 px-2 py-1 text-sm leading-relaxed text-ink outline-none transition-colors focus:bg-black/40"
      />
      <p className="mt-1 text-xs text-ink-muted">{t('messages.editHint')}</p>
    </form>
  );
}
